import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/SEOHead';
import { blogPosts, blogCategories } from '../../mocks/blog';

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All' ? blogPosts : blogPosts.filter(p => p.category === activeCategory);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Electrician Solutions Fresno — Electrical Tips & Guides',
    description: 'Expert electrical guides, safety tips, and how-to articles for Fresno, CA homeowners and businesses.',
    url: 'https://electriciansolutionsfresno.com/blog',
    publisher: {
      '@type': 'Organization',
      name: 'Electrician Solutions Fresno',
      url: 'https://electriciansolutionsfresno.com',
    }
  };

  const featuredPost = blogPosts[0];

  return (
    <>
      <SEOHead
        title="Fresno Electrician Blog | Electrical Tips, Guides & Safety | Electrician Solutions"
        description="Expert electrical guides and safety tips for Fresno CA homeowners. Learn about panel upgrades, EV chargers, solar wiring, smart homes, and more from licensed Fresno electricians."
        canonical="/blog"
        keywords="electrician blog Fresno, electrical tips Fresno CA, electrical safety guides, home electrical advice Fresno"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />

        {/* Hero */}
        <section className="pt-32 pb-16 bg-[#1a1a1a] relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-20 w-64 h-64 rounded-full border-2 border-[#FFD700]"></div>
            <div className="absolute bottom-10 right-20 w-48 h-48 rounded-full border-2 border-[#FFD700]"></div>
          </div>
          <div className="max-w-[1200px] mx-auto px-6 relative z-10">
            <div className="text-center">
              <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full mb-5">
                Fresno Electrician Blog
              </span>
              <h1 className="text-4xl sm:text-6xl font-black text-white mb-5 leading-tight">
                ELECTRICAL TIPS &amp;<br />GUIDES FOR FRESNO
              </h1>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                Expert advice on home electrical safety, panel upgrades, EV chargers, solar wiring, and smart home automation from Fresno's licensed C-10 electricians.
              </p>
            </div>
          </div>
        </section>

        {/* Featured Post */}
        <section className="py-14 bg-[#f5f5f5]">
          <div className="max-w-[1200px] mx-auto px-6">
            <h2 className="text-xs font-black uppercase tracking-widest text-[#FFD700] mb-6">Featured Article</h2>
            <Link to={`/blog/${featuredPost.slug}`} className="group grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden hover:scale-[1.01] transition-transform duration-300 cursor-pointer">
              <div className="h-72 lg:h-auto overflow-hidden">
                <img
                  src={featuredPost.image}
                  alt={featuredPost.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="bg-[#1a1a1a] p-8 sm:p-12 flex flex-col justify-center">
                <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full mb-4 w-fit">
                  {featuredPost.category}
                </span>
                <h3 className="text-2xl sm:text-3xl font-black text-white mb-4 leading-tight group-hover:text-[#FFD700] transition-colors">
                  {featuredPost.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-6">{featuredPost.excerpt}</p>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-xs">{featuredPost.publishDate} · {featuredPost.readTime}</span>
                  <span className="text-[#FFD700] font-bold text-sm flex items-center gap-1">
                    Read Article <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* Category Filter */}
        <section className="pt-10 pb-2 bg-white">
          <div className="max-w-[1200px] mx-auto px-6">
            <div className="flex flex-wrap gap-2">
              {blogCategories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${activeCategory === cat ? 'bg-[#FFD700] text-[#1a1a1a]' : 'bg-[#f5f5f5] text-gray-600 hover:bg-[#FFD700] hover:text-[#1a1a1a]'}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Posts Grid */}
        <section className="py-10 bg-white">
          <div className="max-w-[1200px] mx-auto px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map(post => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-xl overflow-hidden border-2 border-gray-100 hover:border-[#FFD700] transition-all duration-200 cursor-pointer"
                >
                  <div className="h-52 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full mb-3">
                      {post.category}
                    </span>
                    <h3 className="font-black text-[#1a1a1a] text-base leading-snug mb-3 group-hover:text-[#1a1a1a]">
                      {post.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-2">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs">{post.publishDate} · {post.readTime}</span>
                      <span className="text-[#FFD700] font-bold text-xs flex items-center gap-1">
                        Read <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Internal Linking — Services CTA */}
        <section className="py-14 bg-[#1a1a1a]">
          <div className="max-w-[1200px] mx-auto px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-black text-white mb-3">
                Ready to Book a Licensed Fresno Electrician?
              </h2>
              <p className="text-gray-400 text-sm max-w-xl mx-auto">
                Explore our full range of electrical services for Fresno homes and businesses.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'Residential Electrical', path: '/services/residential-electrical', icon: 'ri-home-4-line' },
                { label: 'Panel Upgrades', path: '/services/residential-electrical/panel-upgrades', icon: 'ri-plug-line' },
                { label: 'EV Charger Install', path: '/services/renewables-ev/ev-charger-installation', icon: 'ri-car-line' },
                { label: 'Smart Home', path: '/services/smart-home-energy', icon: 'ri-smartphone-line' },
                { label: 'Repairs & Maintenance', path: '/services/repairs-maintenance', icon: 'ri-tools-line' },
                { label: 'Fire Safety', path: '/services/fire-safety', icon: 'ri-fire-line' },
                { label: 'Solar Electrical', path: '/services/renewables-ev/solar-panel-electrical-connections', icon: 'ri-sun-line' },
                { label: 'Commercial', path: '/services/commercial-specialist', icon: 'ri-building-4-line' },
              ].map(item => (
                <Link key={item.path} to={item.path} className="flex items-center gap-3 bg-white/5 hover:bg-[#FFD700]/10 border border-white/10 hover:border-[#FFD700] rounded-xl px-4 py-3 transition-all cursor-pointer group">
                  <span className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-[#FFD700] text-xl`}></i>
                  </span>
                  <span className="text-white text-xs font-bold group-hover:text-[#FFD700] transition-colors">{item.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Area Links */}
        <section className="py-12 bg-white border-t border-gray-100">
          <div className="max-w-[1200px] mx-auto px-6 text-center">
            <h2 className="text-lg font-black text-[#1a1a1a] mb-6">Electrician Services Available Near You</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { name: 'Fresno', path: '/' },
                { name: 'Clovis', path: '/areas/clovis' },
                { name: 'Madera', path: '/areas/madera' },
                { name: 'Visalia', path: '/areas/visalia' },
                { name: 'Hanford', path: '/areas/hanford' },
                { name: 'Tulare', path: '/areas/tulare' },
                { name: 'Selma', path: '/areas/selma' },
                { name: 'Sanger', path: '/areas/sanger' },
                { name: 'Reedley', path: '/areas/reedley' },
                { name: 'Dinuba', path: '/areas/dinuba' },
                { name: 'Porterville', path: '/areas/porterville' },
              ].map(area => (
                <Link key={area.path} to={area.path} className="px-4 py-2 bg-[#f5f5f5] hover:bg-[#FFD700] text-sm font-bold text-[#1a1a1a] rounded-full transition-colors cursor-pointer whitespace-nowrap">
                  Electrician in {area.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default BlogPage;
